// src/pages/Exams/generate/MathText.tsx
import { useMemo } from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';

interface Props {
  children: string;
  className?: string;
}

interface Seg { type: 'text' | 'inline' | 'block'; value: string; }

// A JSON "\\times" mangled upstream arrives as TAB + "imes". Inside a math
// segment a control character can never be intended, so map it back to the
// backslash command it came from. \n is only restored before known commands
// so that real line breaks are never touched.
const NEWLINE_COMMANDS = /^(eq|abla|u|ot|otin|i|eg|ewline|less|leq|geq|mid|rightarrow|exists|subseteq)(?![A-Za-z])/;
export function repairMathControlChars(math: string): string {
  return math
    .replace(/\t(?=[A-Za-z])/g, '\\t')
    .replace(/\f(?=[A-Za-z])/g, '\\f')
    .replace(/\x08(?=[A-Za-z])/g, '\\b')
    .replace(/\r(?=[A-Za-z])/g, '\\r')
    .replace(/\n(?=[A-Za-z])/g, (m, offset: number, str: string) =>
      NEWLINE_COMMANDS.test(str.slice(offset + 1)) ? '\\n' : m);
}

function tokenize(input: string): Seg[] {
  const segs: Seg[] = [];
  const re = /\$\$([\s\S]+?)\$\$|\$([^$]+?)\$/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(input)) !== null) {
    if (m.index > last) segs.push({ type: 'text', value: input.slice(last, m.index) });
    if (m[1] != null) segs.push({ type: 'block', value: m[1] });
    else segs.push({ type: 'inline', value: m[2] });
    last = re.lastIndex;
  }
  if (last < input.length) segs.push({ type: 'text', value: input.slice(last) });
  return segs;
}

export function MathText({ children, className }: Props) {
  const segs = useMemo(() => tokenize(children ?? ''), [children]);
  return (
    <span className={className}>
      {segs.map((s, i) => {
        if (s.type === 'text') return <span key={i}>{s.value}</span>;
        let html = '';
        try {
          html = katex.renderToString(repairMathControlChars(s.value), {
            throwOnError: false, displayMode: s.type === 'block',
          });
        } catch {
          return <span key={i}>{s.type === 'block' ? `$$${s.value}$$` : `$${s.value}$`}</span>;
        }
        return <span key={i} dangerouslySetInnerHTML={{ __html: html }} />;
      })}
    </span>
  );
}
